// TODO §18 — wire react-secure-storage once the encryption-at-rest decision is made.
// Until then this is a thin Web-Storage-backed shim with the final API surface,
// so country code can target it now and we swap the implementation later.

/**
 * Which browser store backs a namespace.
 *
 * - `'session'` (default) — dies with the tab. Nothing reaches disk, and the
 *   next person to open this browser inherits nothing.
 * - `'local'` — survives the tab, the browser and the machine restarting. Use
 *   it only where the data outliving one tab is the POINT (an identity a
 *   remembered login should still know on a cold start) and the owner has an
 *   explicit way to remove it again — a sign-out path that calls `remove`.
 *   Anything written here is readable from devtools until something deletes it.
 */
export type StorageBackend = 'session' | 'local';

export interface StorageOptions {
  readonly backend?: StorageBackend;
}

export interface StorageNamespace {
  get<T>(key: string, schema?: { parse: (input: unknown) => T }): T | null;
  set<T>(key: string, value: T): void;
  remove(key: string): void;
  clear(): void;
}

const stores = new Map<string, StorageNamespace>();

function backendFor(kind: StorageBackend): Storage | null {
  if (typeof window === 'undefined') return null;
  return kind === 'local' ? window.localStorage : window.sessionStorage;
}

export function createStorage(namespace: string, options: StorageOptions = {}): StorageNamespace {
  const kind: StorageBackend = options.backend ?? 'session';
  // Keyed by BOTH, so one namespace can hold a session-scoped and a persistent
  // half without the second caller silently receiving the first one's store.
  const id = `${kind}:${namespace}`;
  const existing = stores.get(id);
  if (existing) return existing;

  const prefix = `fluxora:${namespace}:`;
  const backend = backendFor(kind);

  const ns: StorageNamespace = {
    get(key, schema) {
      if (!backend) return null;
      const raw = backend.getItem(prefix + key);
      if (raw === null) return null;
      try {
        const parsed: unknown = JSON.parse(raw);
        return schema ? schema.parse(parsed) : (parsed as never);
      } catch {
        return null;
      }
    },
    set(key, value) {
      if (!backend) return;
      backend.setItem(prefix + key, JSON.stringify(value));
    },
    remove(key) {
      if (!backend) return;
      backend.removeItem(prefix + key);
    },
    clear() {
      if (!backend) return;
      for (let i = backend.length - 1; i >= 0; i -= 1) {
        const k = backend.key(i);
        if (k && k.startsWith(prefix)) backend.removeItem(k);
      }
    },
  };

  stores.set(id, ns);
  return ns;
}
